import React, { useState } from 'react';
import { Luggage, Truck, MapPin, Clock, CheckCircle2, Phone, Mail, ArrowRight, Globe, Shield, DollarSign, Package } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { Helmet } from 'react-helmet-async';

const ShipSuitcaseCanadaToIndiaPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    weight: ''
  });
  const { toast } = useToast();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Quote Request Received!",
      description: "Our team will contact you within 24 hours with suitcase shipping rates to India."
    });
    setFormData({ name: '', email: '', phone: '', weight: '' });
  };

  const benefits = [
    {
      icon: DollarSign,
      title: "Affordable Rates",
      description: "Cost-effective suitcase shipping from $7.99/kg (airport-to-airport) or $8.49/kg (doorstep delivery) for bulk shipments. Much cheaper than airline baggage fees."
    },
    {
      icon: Luggage,
      title: "Ship Any Suitcase",
      description: "We handle 23kg, 30kg, and larger suitcases. Perfect for students, relocations, and sending gifts to family in India."
    },
    {
      icon: Truck,
      title: "Door-to-Door Delivery",
      description: "Convenient pickup from your Canadian address and direct delivery to any location across India."
    },
    {
      icon: Globe,
      title: "Trusted Carriers",
      description: "Partner with DHL, FedEx, UPS, and specialized freight services for secure and reliable suitcase delivery."
    },
    {
      icon: Shield,
      title: "Customs Assistance",
      description: "Expert support with customs documentation and clearance procedures for smooth delivery in India."
    },
    {
      icon: MapPin,
      title: "Real-Time Tracking",
      description: "Track your suitcase shipment from Canada to India with live updates throughout the journey."
    }
  ];

  const steps = [
    {
      number: 1,
      title: "Measure Your Suitcase",
      description: "Weigh your suitcase and note the dimensions. We calculate shipping based on actual or dimensional weight."
    },
    {
      number: 2,
      title: "Get Instant Quote",
      description: "Contact us with your suitcase details. We compare rates from multiple carriers to find the best price."
    },
    {
      number: 3,
      title: "Secure Packaging",
      description: "Ensure your suitcase is properly closed and secured. We can provide additional packaging if needed."
    },
    {
      number: 4,
      title: "Schedule Pickup",
      description: "We arrange pickup from your home, office, or you can drop off at our partner locations across Canada."
    },
    {
      number: 5,
      title: "Track & Deliver",
      description: "Receive tracking information and monitor your suitcase shipment until it arrives safely in India."
    }
  ];

  const suitcaseTypes = [
    { type: '23kg Standard Suitcase', description: 'Standard airline-size suitcase, ideal for personal belongings and clothing' },
    { type: '30kg Large Suitcase', description: 'Larger suitcases for extended trips, relocations, or sending gifts to family' },
    { type: 'Multiple Suitcases', description: 'Ship 2, 3, or more suitcases at discounted bulk rates to India' },
    { type: 'Student Luggage', description: 'Special rates for students shipping belongings to India during breaks or after graduation' }
  ];

  const allowedItems = [
    'Clothing and textiles',
    'Shoes and accessories',
    'Books and educational materials',
    'Non-perishable food items',
    'Electronics (properly packed)',
    'Personal care products',
    'Household items',
    'Gifts for family and friends'
  ];

  const faqs = [
    {
      question: "How much does it cost to ship a suitcase from Canada to India?",
      answer: "Suitcase shipping costs depend on weight, dimensions, and destination. For a 23kg suitcase to India, our doorstep delivery rate is $9.99/kg (approximately $230). For parcels 1-15kg, we offer fixed-tier pricing. For bulk shipments over 100kg to India airport-to-airport, rates start at $7.99/kg. We compare DHL, FedEx, UPS, and freight options to provide the most competitive pricing."
    },
    {
      question: "Can I ship a 23kg suitcase to India?",
      answer: "Absolutely! We specialize in shipping 23kg suitcases from Canada to India. This is the standard airline baggage weight limit, and we can ship individual or multiple 23kg suitcases at competitive rates with door-to-door delivery."
    },
    {
      question: "How long does suitcase shipping from Canada to India take?",
      answer: "Delivery times vary by service: Express shipping takes 5-10 business days, economy shipping takes 15-25 business days, and freight options may take 4-8 weeks. We'll help you choose the best option based on your timeline and budget."
    },
    {
      question: "Is shipping a suitcase cheaper than airline baggage fees?",
      answer: "Yes, in many cases! Airline excess baggage fees can cost $100-$300+ per bag. Shipping suitcases separately through Shippzy is often more economical, especially for multiple bags, heavy items, or when you have flexible timing."
    },
    {
      question: "What items can I pack in a suitcase for shipping to India?",
      answer: "You can pack clothing, shoes, books, non-perishable foods, electronics (properly packed), personal care items, household goods, and gifts. Prohibited items include liquids, flammable materials, weapons, cash, and perishable foods. Contact us for specific restrictions."
    },
    {
      question: "Do I need to pay customs duty on suitcases sent to India?",
      answer: "Customs duties may apply depending on the contents and value. Personal belongings and used items typically have lower duties. We help you prepare proper customs documentation. Duty amounts are determined by Indian customs authorities and are the responsibility of the recipient."
    },
    {
      question: "Can students ship their luggage to India?",
      answer: "Yes! We offer student-friendly rates for shipping suitcases and luggage to India. Many international students use our service to send belongings home during breaks or after graduation, avoiding airline baggage limits and fees."
    },
    {
      question: "How do I pack a suitcase for international shipping?",
      answer: "Use a sturdy, hard-sided suitcase when possible. Secure all zippers and locks. Remove external tags and straps. Pack fragile items with bubble wrap. We recommend additional outer packaging for extra protection during transit."
    }
  ];

  const [openFaq, setOpenFaq] = useState(null);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Ship Suitcase to India from Canada | Luggage Shipping – Shippzy</title>
        <meta name="description" content="Ship suitcase and luggage from Canada to India. Affordable rates for 23kg, 30kg suitcases. Door-to-door delivery, cheaper than airline fees. Perfect for students and relocations. Get quote today." />
        <meta name="keywords" content="ship suitcase Canada to India, send luggage to India, 23kg suitcase shipping India, 30kg luggage to India, student luggage shipping, excess baggage shipping Canada India, ship bags to India" />
        <link rel="canonical" href="https://www.shippzy.ca/ship-suitcase-canada-to-india" />
        
        <meta property="og:title" content="Ship Suitcase to India | Affordable Luggage Shipping – Shippzy" />
        <meta property="og:description" content="Ship your suitcase from Canada to India. Competitive rates, door-to-door service, cheaper than airline baggage fees. 23kg, 30kg, and bulk shipping available." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.shippzy.ca/ship-suitcase-canada-to-india" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Suitcase Shipping to India from Canada",
            "provider": {
              "@type": "Organization",
              "name": "Shippzy Inc.",
              "telephone": "+1-437-616-0758",
              "email": "ship@shippzy.ca"
            },
            "areaServed": ["CA", "IN"],
            "serviceType": "Luggage and Suitcase Shipping",
            "offers": {
              "@type": "Offer",
              "priceSpecification": {
                "@type": "PriceSpecification",
                "price": "9.99",
                "priceCurrency": "CAD",
                "unitText": "per kg",
                "minWeight": "23 kg minimum"
              }
            }
          })}
        </script>
        
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <div className="inline-block bg-gradient-to-r from-red-600 to-yellow-500 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
                🧳 Affordable Suitcase & Luggage Shipping
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Ship Suitcase from Canada to India
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                Shippzy makes it easy and affordable to ship suitcases and luggage from Canada to India. Whether you have a standard 23kg suitcase, a 30kg bag, or multiple pieces of luggage, we provide cost-effective shipping solutions that are often cheaper than airline baggage fees. Perfect for students, relocations, and sending gifts to family. Door-to-door service with customs assistance and real-time tracking included.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white px-8 py-6 text-lg"
                  onClick={() => document.getElementById('quote-form').scrollIntoView({ behavior: 'smooth' })}
                >
                  Get Instant Quote
                </Button>
                <Button 
                  variant="outline" 
                  className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-lg"
                  onClick={() => window.location.href = 'tel:+14376160758'}
                >
                  Call: +1 (437) 616-0758
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Ship Your Suitcase with Shippzy</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Save money, avoid airline hassles, and ship your luggage safely from Canada to India
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <Card key={index} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <CardContent className="pt-6">
                      <div className="w-14 h-14 bg-gradient-to-r from-red-600 to-yellow-500 rounded-lg flex items-center justify-center mb-4">
                        <IconComponent className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Suitcase Types Section */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Suitcase Sizes We Ship to India</h2>
              <p className="text-xl text-gray-600">
                From standard 23kg to large 30kg+ suitcases and bulk shipments
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {suitcaseTypes.map((type, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                      <Package className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">{type.type}</h3>
                        <p className="text-sm text-gray-600">{type.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 bg-gradient-to-br from-red-50 to-yellow-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">How Suitcase Shipping to India Works</h2>
              <p className="text-xl text-gray-600">Simple 5-step process</p>
            </div>

            <div className="space-y-6">
              {steps.map((step, index) => (
                <Card key={index} className="border-2 hover:border-red-300 transition-colors">
                  <CardContent className="pt-6">
                    <div className="flex gap-6 items-start">
                      <div className="w-12 h-12 bg-gradient-to-r from-red-600 to-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-2xl font-bold text-white">{step.number}</span>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                        <p className="text-gray-600">{step.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What Can You Pack */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">What Can You Pack in Your Suitcase</h2>
              <p className="text-xl text-gray-600">
                Suitable items for suitcase shipping to India
              </p>
            </div>

            <Card className="shadow-xl">
              <CardContent className="pt-8">
                <div className="grid md:grid-cols-2 gap-4">
                  {allowedItems.map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                      <span className="text-gray-800">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-8 p-4 bg-yellow-100 border-l-4 border-yellow-500 rounded">
                  <p className="text-sm text-gray-800">
                    <strong>Prohibited Items:</strong> Liquids, flammable materials, weapons, explosives, perishable foods, cash, and certain electronics with large lithium batteries. Contact us for complete restrictions.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-xl text-gray-600">Everything about suitcase shipping from Canada to India</p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <Card key={index} className="border-2 hover:border-red-300 transition-colors cursor-pointer" onClick={() => setOpenFaq(openFaq === index ? null : index)}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</h3>
                      <ArrowRight className={`w-5 h-5 text-red-600 flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-90' : ''}`} />
                    </div>
                    {openFaq === index && (
                      <p className="text-gray-600 mt-4 leading-relaxed">{faq.answer}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Quote Form */}
        <section id="quote-form" className="py-20 bg-gradient-to-r from-red-600 to-yellow-500">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center text-white mb-8">
              <h2 className="text-4xl font-bold mb-4">Ready to Ship Your Suitcase to India?</h2>
              <p className="text-xl">Get your free shipping quote today</p>
            </div>

            <Card className="shadow-2xl">
              <CardHeader>
                <CardTitle className="text-2xl text-center">Get Your Free Quote</CardTitle>
                <CardDescription className="text-center">Best rates for suitcase shipping to India</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="+1 (123) 456-7890"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Approximate Weight (kg)</label>
                    <Input
                      type="number"
                      name="weight"
                      value={formData.weight}
                      onChange={handleChange}
                      placeholder="e.g., 23, 30"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white h-12 text-lg"
                  >
                    Get Quote Now
                  </Button>
                </form>
              </CardContent>
            </Card>

            <div className="mt-8 text-center text-white">
              <p className="text-lg mb-4">Or contact us directly:</p>
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <a href="tel:+14376160758" className="flex items-center justify-center gap-2 bg-white text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                  <Phone className="w-5 h-5" />
                  +1 (437) 616-0758
                </a>
                <a href="mailto:ship@shippzy.ca" className="flex items-center justify-center gap-2 bg-white text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                  <Mail className="w-5 h-5" />
                  ship@shippzy.ca
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-12 bg-gray-900 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Affordable Suitcase & Luggage Shipping from Canada to India</h3>
            <p className="text-gray-300 mb-6">
              Ship 23kg, 30kg suitcases and luggage at competitive rates. Door-to-door service, cheaper than airline baggage fees. Perfect for students, relocations, and sending gifts to family in India.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button 
                className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white px-8 py-3"
                onClick={() => window.location.href = '/relocation-shipping-canada-to-india'}
              >
                Explore Relocation Shipping
              </Button>
              <Button 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-3"
                onClick={() => window.location.href = '/shipping-to-india-from-canada'}
              >
                Canada to India Shipping
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ShipSuitcaseCanadaToIndiaPage;